import React, { useEffect, useRef, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Helmet from 'react-helmet'
import { useLocation, useParams, withRouter } from 'react-router-dom'

import Slide from '@material-ui/core/Slide'
import cogoToast from 'cogo-toast'
import { Formik } from 'formik'
import { isEmpty, isNil } from 'lodash'
import get from 'lodash/fp/get'
import { set } from 'object-path-immutable'

import CaseSchema from '../CaseSchema'
import MagicWand from '../components/common/MagicWand'
import ConfirmationModal from '../components/controls/ConfirmationModal'
import IntakeFormsvalidationSchemas from '../components/IntakeForms/IntakeFormsvalidationSchemas'
import ReferralForm from '../components/IntakeForms/ReferralForm'
import NewAdjusterModal from '../components/modals/NewAdjusterModal'
import NewCompanyModal from '../components/modals/NewCompanyModal'
import NavigationBar from '../components/NavigationBar'
import AdjustersService from '../services/AdjustersService'
import CasesService from '../services/CasesService'
import ReferralSourcesService from '../services/ReferralSourcesService'
import { cleanPhone, formatPhone } from '../utils/PhoneUtils'

import './IntakeForm.scss'

const defaultCaseModel = (() => {
  const outputModel = {}
  CaseSchema.forEach(({ name, value }) => {
    if (get(name, outputModel) === undefined) {
      set(outputModel, name, value !== undefined ? value : '')
    }
  })
  return outputModel
})()

function setDefaultsOnCase(caseModel) {
  let outputModel = caseModel
  CaseSchema.forEach(({ name, value }) => {
    if (get(name, outputModel) === undefined) {
      outputModel = set(outputModel, name, value !== undefined ? value : '')
    }
  })
  return outputModel
}

const STATE_MAP = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  louisiana: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  ohio: 'OH',
  oklahoma: 'OK',
  oregon: 'OR',
  pennsylvania: 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermont: 'VT',
  virginia: 'VA',
  washington: 'WA',
  'west virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
  'district of columbia': 'DC',
  'american samoa': 'AS',
  guam: 'GU',
  'northern mariana islands': 'MP',
  'puerto rico': 'PR',
  'virgin islands': 'VI',
  'federated states of micronesia': 'FM',
  'marshall islands': 'MH',
  palau: 'PW',
  'armed forces americas': 'AA',
  'armed forces europe': 'AE',
  'armed forces pacific': 'AP',
}

function normalizeCaseStates(caseModel) {
  if (!caseModel) return caseModel
  let model = { ...caseModel }

  const normalize = state => {
    if (!state) return state
    const cleaned = state.trim().toLowerCase()
    if (cleaned.length === 2) return state.toUpperCase()
    return STATE_MAP[cleaned] || state
  }

  if (model.client?.address?.state) {
    model = set(model, 'client.address.state', normalize(model.client.address.state))
  }
  if (model.employer?.address?.state) {
    model = set(model, 'employer.address.state', normalize(model.employer.address.state))
  }
  if (model.plaintiff?.address?.state) {
    model = set(model, 'plaintiff.address.state', normalize(model.plaintiff.address.state))
  }
  if (model.defendant?.address?.state) {
    model = set(model, 'defendant.address.state', normalize(model.defendant.address.state))
  }

  return model
}

const IntakeForm = withRouter(({ history, user }) => {
  const { id } = useParams()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const queryTypeId = params.get('caseTypeId')
  const queryTypeName = params.get('caseTypeName')
  const isOngoingCaseMode = params.get('mode') === 'ongoing'
  const showModel = params.get('showModel')
  const showValidations = params.get('showValidations')
  // Where to return after Smart Intake or cancel — threaded from CaseTypePicker via location.state.
  // Falls back to go(-2) to skip both IntakeForm and select-type if navigated via a hard link.
  const returnTo = location.state?.from ?? null

  // If no case type selected and it's a new case, redirect to the picker
  useEffect(() => {
    if (!id && !queryTypeId) {
      history.replace('/case/intake/select-type')
    }
  }, [id, queryTypeId, history])

  const stepSchema = IntakeFormsvalidationSchemas.getForStep()

  const [initialCaseModel, setInitialCaseModel] = useState(() => {
    const base = JSON.parse(JSON.stringify(defaultCaseModel))
    if (queryTypeId) base.caseTypeId = parseInt(queryTypeId, 10)
    if (isOngoingCaseMode) base.skipInitialReport = true
    return base
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [referralSources, setReferralSources] = useState([])
  const [adjusters, setAdjusters] = useState([])
  const [caseTypeName, setCaseTypeName] = useState(queryTypeName ? decodeURIComponent(queryTypeName) : '')
  const formRef = useRef(null)
  const isDirtyCheckRef = useRef()
  const [showNewReferralSourceModal, setShowNewReferralSourceModal] = useState(false)
  const [showNewAdjusterModal, setShowNewAdjusterModal] = useState(false)
  const [selectedAdjuster, setSelectedAdjuster] = useState({})
  const [selectedReferralSource, setSelectedReferralSource] = useState({})
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false)
  const fileInputRef = useRef(null)
  const [stagedFiles, setStagedFiles] = useState([])
  const [isDragOver, setIsDragOver] = useState(false)
  // Smart Paste state
  const [isExtracting, setIsExtracting] = useState(false)
  const [smartPasteToast, setSmartPasteToast] = useState(null)
  const smartPastePromiseRef = useRef(null)
  const smartPastePatchRef = useRef({})

  useEffect(() => {
    const referralSourcesPromise = ReferralSourcesService.getAll().then(r => {
      setReferralSources(sortByName(r))
    })

    const adjustersPromise = AdjustersService.getAll().then(r => {
      setAdjusters(sortByName(r))
    })

    const casePromise = (() => {
      if (id) {
        return CasesService.get(id).then(r => {
          const normalized = normalizeCaseStates(r)
          const withDefaults = setDefaultsOnCase(normalized)
          setInitialCaseModel(withDefaults)
          // For editing an existing case, derive the case type name from the loaded model
          if (r.caseTypeName) setCaseTypeName(r.caseTypeName)
        })
      }
      setInitialCaseModel(prev => setDefaultsOnCase(prev))
      return Promise.resolve()
    })()

    Promise.all([referralSourcesPromise, casePromise, adjustersPromise])
      .then(() => setIsLoading(false))
      .catch(err => {
        setError(err.message)
        setIsLoading(false)
      })
    // eslint-disable-next-line
  }, [id])

  const onSubmit = caseModel => {
    setInitialCaseModel(caseModel)
    setIsLoading(true)

    // Clean phone numbers
    const phoneFields = ['client.phone', 'client.fax']
    let finalModel = { ...caseModel }
    phoneFields.forEach(path => {
      const val = get(path, finalModel)
      if (val) finalModel = set(finalModel, path, cleanPhone(val))
    })

    const caseModelToSave = { ...finalModel, tasks: undefined, intakeActivities: undefined }

    const savePromise = id ? CasesService.update(caseModelToSave) : CasesService.create(caseModelToSave)

    savePromise
      .onSuccess(savedCase => {
        setError(null)
        // Navigate directly to the case overview (Draft state)
        history.push(`/cases/${savedCase.caseNumber}`)
      })
      .onBadRequest(err => setError(err))
      .catch(() => setError('An unexpected error occurred while trying to save the case'))
      .then(() => setIsLoading(false))
  }

  const scrollToField = fieldName => {
    const label = document.querySelector(`[for="${fieldName}"]`)
    if (!isNil(label)) label.scrollIntoView({ behavior: 'smooth', block: 'center' })
    let input = document.querySelector(`[name="${fieldName}"]`)
    if (isNil(input)) input = document.querySelector(`[id="${fieldName}"]`)
    if (!isNil(input)) input.focus()
  }

  const getFieldPaths = obj => {
    const paths = []
    function recurse(o, currentPath) {
      for (const key in o) {
        const path = isEmpty(currentPath) ? key : currentPath + '.' + key
        if (typeof o[key] === 'object') recurse(o[key], path)
        else paths.push(path)
      }
    }
    recurse(obj, '')
    return paths
  }

  const handleClickRight = e => {
    e.preventDefault()
    formRef.current.submitForm()
  }

  const sortByName = source => {
    return Array.from(source).sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
  }

  const handleClose = () => {
    if (isDirtyCheckRef.current && isDirtyCheckRef.current()) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to exit?')) return
    }
    if (returnTo) history.push(returnTo)
    else history.go(-2) // skip IntakeForm + select-type back to wherever New Case was clicked
  }

  const handleSmartIntake = () => {
    CasesService.warmupSmartIntake()
    if (fileInputRef.current) fileInputRef.current.click()
  }

  const addFilesToStaging = incomingFiles => {
    const list = Array.from(incomingFiles || []).filter(Boolean)
    if (list.length === 0) return
    CasesService.warmupSmartIntake()
    setStagedFiles(prev => {
      const existingKeys = new Set(prev.map(f => `${f.name}_${f.size}_${f.lastModified}`))
      const additions = list.filter(f => !existingKeys.has(`${f.name}_${f.size}_${f.lastModified}`))
      return [...prev, ...additions]
    })
  }

  const handleFileChange = e => {
    if (e.target.files && e.target.files.length > 0) {
      addFilesToStaging(e.target.files)
    }
    e.target.value = null
  }

  const handleRemoveStagedFile = indexToRemove => {
    setStagedFiles(prev => prev.filter((_, idx) => idx !== indexToRemove))
  }

  const handleStartSmartIntake = async () => {
    if (!stagedFiles || stagedFiles.length === 0) return
    if (smartPastePromiseRef.current) {
      try {
        await smartPastePromiseRef.current
      } catch {
        // Ignore Smart Paste failure and proceed with current form values
      }
    }
    let currentData = formRef.current ? formRef.current.values : initialCaseModel
    const patch = smartPastePatchRef.current || {}
    if (Object.keys(patch).length > 0) {
      currentData = {
        ...currentData,
        referral: { ...(currentData.referral || {}), ...(patch.referral || {}) },
        client: { ...(currentData.client || {}), ...(patch.client || {}) },
        injury: { ...(currentData.injury || {}), ...(patch.injury || {}) },
        employer: { ...(currentData.employer || {}), ...(patch.employer || {}) },
      }
    }
    const fileCount = stagedFiles.length
    cogoToast.loading(
      `Smart Intake started for ${fileCount} ${
        fileCount === 1 ? 'file' : 'files'
      }. You will be notified when it is ready.`,
      {
        position: 'top-center',
        hideAfter: 5,
      },
    )
    CasesService.smartIntake(stagedFiles, currentData)
      .then(() => {
        if (returnTo) history.push(returnTo)
        else history.go(-2) // skip IntakeForm + select-type
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('Smart Intake Error', err)
        cogoToast.error(`Upload Failed: ${err.message}`, { position: 'top-center', hideAfter: 10 })
      })
  }

  // ---- Smart Paste ✨ ---------------------------------------------------

  const applySmartPasteResult = async (result, formik) => {
    const { setFieldValue } = formik
    const setFieldAndPatch = (path, val) => {
      setFieldValue(path, val)
      smartPastePatchRef.current = set(smartPastePatchRef.current || {}, path, val)
    }
    const extracted = []

    const toIsoDate = mmddyyyy => {
      if (!mmddyyyy) return null
      const parts = String(mmddyyyy).trim().split('/')
      if (parts.length === 3) {
        return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`
      }
      return null
    }

    if (result.referralDate) {
      const iso = toIsoDate(result.referralDate)
      if (iso) {
        setFieldAndPatch('referral.date', iso)
        extracted.push('Date of Referral')
      }
    }
    if (result.claimNumber) {
      setFieldAndPatch('referral.claimNumber', result.claimNumber)
      extracted.push('Claim #')
    }
    if (result.policyNumber) {
      setFieldAndPatch('referral.policyNumber', result.policyNumber)
      extracted.push('Policy #')
    }
    if (result.dueDate) {
      const iso = toIsoDate(result.dueDate)
      if (iso) {
        setFieldAndPatch('referral.dueDate', iso)
        extracted.push('Due Date')
      }
    }
    if (result.clientFirstName) {
      setFieldAndPatch('client.firstName', result.clientFirstName)
      extracted.push('First Name')
    }
    if (result.clientLastName) {
      setFieldAndPatch('client.lastName', result.clientLastName)
      extracted.push('Last Name')
    }
    if (result.clientBirthdate) {
      const iso = toIsoDate(result.clientBirthdate)
      if (iso) {
        setFieldAndPatch('client.birthdate', iso)
        extracted.push('Date of Birth')
      }
    }
    if (result.injuryDate) {
      const iso = toIsoDate(result.injuryDate)
      if (iso) {
        setFieldAndPatch('injury.date', iso)
        extracted.push('Date of Injury')
      }
    }
    if (result.employerDateOfHire) {
      const iso = toIsoDate(result.employerDateOfHire)
      if (iso) {
        setFieldAndPatch('employer.dateOfHire', iso)
        extracted.push('Date of Hire')
      }
    }
    if (result.employerOccupation) {
      setFieldAndPatch('employer.occupation', result.employerOccupation)
      extracted.push('Occupation')
    }

    const normalize = s =>
      String(s || '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
    let matchedSourceId = formik.values?.referral?.sourceId || null

    if (result.adjusterCompany) {
      const companyKey = normalize(result.adjusterCompany)
      if (companyKey) {
        const byCompany = referralSources.find(s => {
          const sKey = normalize(s.name)
          return sKey && sKey === companyKey
        })
        if (byCompany) {
          matchedSourceId = byCompany.id
        }
      }
    }

    if (matchedSourceId) {
      setFieldAndPatch('referral.sourceId', matchedSourceId)
      extracted.push('Referral Source')
    }

    if (result.adjusterName) {
      const key = normalize(result.adjusterName)
      const match = adjusters.find(a => normalize(a.name) === key)
      if (match) {
        setFieldAndPatch('referral.adjusterId', match.id)
        if (match.referralSourceId) setFieldAndPatch('referral.sourceId', match.referralSourceId)
        extracted.push('Adjuster')
      } else {
        setSelectedAdjuster({
          name: result.adjusterName.trim(),
          referralSourceId: matchedSourceId || null,
          email: result.adjusterEmail || '',
          phone: result.adjusterPhone ? formatPhone(result.adjusterPhone) : '',
          fax: result.adjusterFax ? formatPhone(result.adjusterFax) : '',
          addressLine1: result.adjusterAddressLine1 || '',
          addressCity: result.adjusterAddressCity || '',
          addressState: result.adjusterAddressState || '',
          addressZip: result.adjusterAddressZip || '',
        })
        setShowNewAdjusterModal(true)
        extracted.push(`Adjuster "${result.adjusterName}" (new—modal opened)`)
      }
    }

    return extracted
  }

  const handleSmartPasteClick = async () => {
    let text
    try {
      text = await navigator.clipboard.readText()
    } catch {
      setSmartPasteToast({ message: 'Could not read clipboard. Copy the referral email first.', type: 'error' })
      setTimeout(() => setSmartPasteToast(null), 5000)
      return
    }
    if (!text.trim()) {
      setSmartPasteToast({ message: 'Clipboard has no text. Copy the referral email first.', type: 'warning' })
      setTimeout(() => setSmartPasteToast(null), 4000)
      return
    }
    setIsExtracting(true)
    const runExtraction = async () => {
      try {
        const result = await CasesService.smartPaste(text)
        const fields = await applySmartPasteResult(result, formRef.current)
        const msg = fields.length > 0 ? `Pasted: ${fields.join(', ')}` : 'No fields recognized.'
        setSmartPasteToast({ message: msg, type: fields.length > 0 ? 'success' : 'warning' })
        setTimeout(() => setSmartPasteToast(null), 5000)
      } catch {
        setSmartPasteToast({ message: 'Extraction failed. Please try again.', type: 'error' })
        setTimeout(() => setSmartPasteToast(null), 5000)
      } finally {
        smartPastePromiseRef.current = null
        setIsExtracting(false)
      }
    }
    smartPastePromiseRef.current = runExtraction()
    await smartPastePromiseRef.current
  }

  // -----------------------------------------------------------------------

  const navBarTitle = () => 'CASE INTAKE'

  const smartIntakeButton = (
    <button
      type="button"
      className="btn btn-outline-primary d-flex align-items-center"
      onClick={handleSmartIntake}
      title="Add documents or reports (from one or more folders, or a .zip) for Smart Intake"
    >
      <MagicWand active={false} />
      <span className="ml-2">{stagedFiles.length > 0 ? 'Add More Files…' : 'Add Files for Smart Intake…'}</span>
    </button>
  )

  const extraRightButtons = (
    <>
      <button
        type="button"
        className="btn btn-outline-primary d-flex align-items-center mr-2"
        onClick={handleSmartPasteClick}
        disabled={isExtracting}
        title="Copy a referral email to clipboard, then click to extract fields"
      >
        {isExtracting ? (
          <>
            <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true" />
            <span>Reading…</span>
          </>
        ) : (
          <>
            <MagicWand active={false} />
            <span className="ml-2">Smart Paste</span>
          </>
        )}
      </button>
      {smartPasteToast && (
        <small
          className={`mr-2 align-self-center ${
            smartPasteToast.type === 'success'
              ? 'text-success'
              : smartPasteToast.type === 'error'
              ? 'text-danger'
              : 'text-warning'
          }`}
        >
          {smartPasteToast.message}
        </small>
      )}
      {smartIntakeButton}
    </>
  )

  return (
    <div>
      <Helmet
        title={
          id
            ? `Intake - Case ${id} - ${get('client.firstName', initialCaseModel) || ''} ${
                get('client.lastName', initialCaseModel) || ''
              }`
            : 'New Case Intake'
        }
      />
      <Slide className="container-intake-form background" direction="up" in={true} mountOnEnter unmountOnExit>
        <div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            multiple
            style={{ display: 'none' }}
            accept=".pdf,.doc,.docx,.txt,.zip,image/*"
          />
          <NavigationBar
            title={navBarTitle()}
            rightBtnText="CREATE CASE"
            leftBtnText="Cancel"
            handleClickLeft={handleClose}
            handleClickRight={handleClickRight}
            rightBtnSecondaryText={id ? 'Cancel & Delete' : null}
            handleClickRightSecondary={() => setShowCancelConfirmation(true)}
            extraRightContent={null}
            isInline="sticky"
          />

          <div className="container mt-4 mb-4">
            <div className="form-content">
              {isLoading ? (
                <div className="d-flex justify-content-center align-items-center flex-column">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
              ) : (
                <>
                  {error && (
                    <Alert variant="danger">
                      <strong>Error:</strong> <pre>{error}</pre>
                    </Alert>
                  )}
                  <Formik
                    initialValues={initialCaseModel}
                    enableReinitialize={true}
                    validationSchema={stepSchema}
                    onSubmit={onSubmit}
                    validateOnChange={false}
                    validateOnBlur={true}
                    validateOnMount={true}
                  >
                    {({
                      dirty,
                      values,
                      errors,
                      handleSubmit,
                      handleReset,
                      submitForm,
                      setFieldValue,
                      handleChange,
                      resetForm,
                    }) => {
                      const handleReferralSourceCreated = r => {
                        const index = referralSources.findIndex(s => s.id === r.id)
                        if (index !== -1) {
                          const updated = referralSources.slice()
                          updated.splice(index, 1, r)
                          setReferralSources(sortByName(updated))
                        } else {
                          setReferralSources(sortByName(referralSources.concat(r)))
                        }
                        setFieldValue('referral.sourceId', r.id)
                        smartPastePatchRef.current = set(smartPastePatchRef.current || {}, 'referral.sourceId', r.id)
                        setSelectedReferralSource({})
                        setShowNewReferralSourceModal(false)
                      }

                      const handleAdjusterCreated = r => {
                        const index = adjusters.findIndex(s => s.id === r.id)
                        if (index !== -1) {
                          const updated = adjusters.slice()
                          updated.splice(index, 1, r)
                          setAdjusters(sortByName(updated))
                        } else {
                          setAdjusters(sortByName(adjusters.concat(r)))
                        }
                        setFieldValue('referral.adjusterId', r.id)
                        smartPastePatchRef.current = set(smartPastePatchRef.current || {}, 'referral.adjusterId', r.id)
                        if (r.referralSourceId) {
                          setFieldValue('referral.sourceId', r.referralSourceId)
                          smartPastePatchRef.current = set(
                            smartPastePatchRef.current || {},
                            'referral.sourceId',
                            r.referralSourceId,
                          )
                        }
                        setSelectedAdjuster({})
                        setShowNewAdjusterModal(false)
                      }

                      function showAdjusterEditScreen(adjusterId) {
                        const adjuster = adjusters.find(a => a.id === adjusterId) || {}
                        setSelectedAdjuster(adjuster)
                        setShowNewAdjusterModal(true)
                      }

                      function showReferralSourceEditScreen(referralSourceId) {
                        const source = referralSources.find(s => s.id === referralSourceId) || {}
                        setSelectedReferralSource(source)
                        setShowNewReferralSourceModal(true)
                      }

                      isDirtyCheckRef.current = () => dirty

                      formRef.current = {
                        values,
                        setFieldValue,
                        resetForm,
                        submitForm: () => {
                          submitForm().then(() => {
                            if (!isEmpty(errors)) {
                              const firstInvalidField = getFieldPaths(errors)[0]
                              scrollToField(firstInvalidField)
                            }
                          })
                        },
                      }

                      return (
                        <>
                          <form className="form-horizontal intake-form" onSubmit={handleSubmit} onReset={handleReset}>
                            <div
                              className={`card mb-4 shadow-sm ${isDragOver ? 'border-primary' : 'bg-light'}`}
                              onDragOver={e => {
                                e.preventDefault()
                                e.stopPropagation()
                                if (!isDragOver) setIsDragOver(true)
                              }}
                              onDragLeave={e => {
                                e.preventDefault()
                                e.stopPropagation()
                                setIsDragOver(false)
                              }}
                              onDrop={e => {
                                e.preventDefault()
                                e.stopPropagation()
                                setIsDragOver(false)
                                if (e.dataTransfer?.files?.length) {
                                  addFilesToStaging(e.dataTransfer.files)
                                }
                              }}
                            >
                              <div className="card-body">
                                <div
                                  className="d-flex align-items-center justify-content-between flex-wrap"
                                  style={{ gap: '12px' }}
                                >
                                  <div>
                                    <h6 className="card-title mb-1 font-weight-bold">
                                      {isOngoingCaseMode ? 'Smart Import — Ongoing Case Reports' : 'Smart Import'}
                                    </h6>
                                    <p className="card-text text-muted mb-0 small">
                                      {isOngoingCaseMode
                                        ? 'Drop or browse to add your existing Initial Case Report and Progress Reports (across one or more folders, or a .zip archive). Progress Reports will be automatically numbered in chronological date order.'
                                        : 'Automatically pre-fill form fields and file case reports by adding documents (across one or more folders, or a .zip) or pasting referral text.'}
                                    </p>
                                  </div>
                                  <div className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                                    {extraRightButtons}
                                  </div>
                                </div>

                                {(stagedFiles.length > 0 || isOngoingCaseMode || isDragOver) && (
                                  <div
                                    className="mt-3 p-3 rounded"
                                    style={{
                                      border: isDragOver ? '2px dashed #007bff' : '1px dashed #adb5bd',
                                      backgroundColor: isDragOver ? 'rgba(0, 123, 255, 0.05)' : '#ffffff',
                                    }}
                                  >
                                    {stagedFiles.length === 0 ? (
                                      <div className="text-center text-muted small py-2">
                                        <i className="fas fa-cloud-upload-alt mr-2 text-primary" />
                                        Drag &amp; drop your existing <strong>Initial Case Report</strong> and{' '}
                                        <strong>Progress Reports</strong> here (or click{' '}
                                        <strong>Add Files for Smart Intake…</strong> above to select files from one or
                                        more folders).
                                      </div>
                                    ) : (
                                      <>
                                        <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap">
                                          <span className="small font-weight-bold text-dark">
                                            <i className="fas fa-layer-group mr-1 text-primary" />
                                            Staged for Smart Intake ({stagedFiles.length}{' '}
                                            {stagedFiles.length === 1 ? 'file' : 'files'}) — you can drop or browse
                                            additional folders before starting:
                                          </span>
                                          <button
                                            type="button"
                                            className="btn btn-link btn-sm text-danger p-0"
                                            onClick={() => setStagedFiles([])}
                                          >
                                            Clear all
                                          </button>
                                        </div>
                                        <div className="d-flex flex-wrap mb-3" style={{ gap: '8px' }}>
                                          {stagedFiles.map((file, idx) => (
                                            <div
                                              key={`${file.name}_${file.size}_${idx}`}
                                              className="badge badge-light border d-inline-flex align-items-center px-2 py-1"
                                              style={{ fontSize: '0.82rem', fontWeight: 500 }}
                                            >
                                              <i className="far fa-file-alt mr-1 text-primary" />
                                              <span
                                                className="text-truncate"
                                                style={{ maxWidth: '240px' }}
                                                title={file.name}
                                              >
                                                {file.name}
                                              </span>
                                              <span className="text-muted ml-1">
                                                ({Math.max(1, Math.round(file.size / 1024))} KB)
                                              </span>
                                              <button
                                                type="button"
                                                className="btn btn-link btn-sm text-secondary p-0 ml-2"
                                                onClick={() => handleRemoveStagedFile(idx)}
                                                title={`Remove ${file.name}`}
                                                aria-label={`Remove ${file.name}`}
                                              >
                                                <i className="fas fa-times" />
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                        <div
                                          className="d-flex align-items-center justify-content-end"
                                          style={{ gap: '8px' }}
                                        >
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={handleSmartIntake}
                                          >
                                            <i className="fas fa-plus mr-1" /> Add More Files…
                                          </button>
                                          <button
                                            type="button"
                                            id="startSmartIntakeBtn"
                                            className="btn btn-sm btn-primary d-flex align-items-center font-weight-semibold"
                                            onClick={handleStartSmartIntake}
                                            disabled={isExtracting}
                                            title={
                                              isExtracting
                                                ? 'Please wait for Smart Paste to finish populating form fields'
                                                : 'Start Smart Intake with the staged files'
                                            }
                                          >
                                            {isExtracting ? (
                                              <>
                                                <span
                                                  className="spinner-border spinner-border-sm mr-2"
                                                  role="status"
                                                  aria-hidden="true"
                                                />
                                                <span>Waiting for Smart Paste…</span>
                                              </>
                                            ) : (
                                              <>
                                                <MagicWand active={false} />
                                                <span className="ml-2">
                                                  Start Smart Intake ({stagedFiles.length}{' '}
                                                  {stagedFiles.length === 1 ? 'file' : 'files'})
                                                </span>
                                              </>
                                            )}
                                          </button>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            <ReferralForm
                              caseTypeName={caseTypeName}
                              referralSources={referralSources}
                              onAddNewReferralSource={() => setShowNewReferralSourceModal(true)}
                              adjusters={adjusters}
                              onAddNewAdjuster={() => setShowNewAdjusterModal(true)}
                              onEditAdjuster={showAdjusterEditScreen}
                              onEditReferralSource={showReferralSourceEditScreen}
                              handleFieldChange={handleChange}
                              setFieldValue={setFieldValue}
                              values={values}
                              isOngoingCaseMode={isOngoingCaseMode}
                              onAddOngoingReportFiles={handleSmartIntake}
                            />
                            <button
                              id="makeInvalidButton"
                              style={{ display: 'none' }}
                              type="button"
                              onClick={() => setFieldValue('not-a-valid-field', 'made-up-value')}
                            />
                          </form>

                          {showNewReferralSourceModal && (
                            <NewCompanyModal
                              oldModel={selectedReferralSource}
                              onSaveSuccess={handleReferralSourceCreated}
                              onClosed={() => {
                                setSelectedReferralSource({})
                                setShowNewReferralSourceModal(false)
                              }}
                            />
                          )}

                          {showCancelConfirmation && (
                            <ConfirmationModal
                              title="Cancel & Delete Case?"
                              body="Are you sure you want to cancel this case intake? Cancelling will remove all data entered for this case."
                              confirmText="Yes, Delete"
                              cancelText="No, Keep"
                              onConfirm={() => {
                                CasesService.delete(id)
                                  .then(() => history.push('/cases'))
                                  .catch(() => setShowCancelConfirmation(false))
                              }}
                              onClose={() => setShowCancelConfirmation(false)}
                            />
                          )}

                          {showNewAdjusterModal && (
                            <NewAdjusterModal
                              oldModel={selectedAdjuster}
                              onSaveSuccess={handleAdjusterCreated}
                              referralSources={referralSources}
                              onClosed={() => {
                                setSelectedAdjuster({})
                                setShowNewAdjusterModal(false)
                              }}
                            />
                          )}

                          {showModel && <pre>{JSON.stringify(values, null, 2)}</pre>}
                          {showValidations && <pre>{JSON.stringify(errors, null, 2)}</pre>}
                        </>
                      )
                    }}
                  </Formik>
                </>
              )}
            </div>
          </div>
        </div>
      </Slide>
    </div>
  )
})

export default IntakeForm
